/**
 * Data Upload Service
 *
 * API client for the data upload batch management system.
 * Handles CSV file uploads, batch listing, stage triggering, and status polling.
 */

import { get, post, del, upload } from './api';
import type {
  UploadBatch,
  ListBatchesResponse,
  StageTriggerResponse,
  DeleteBatchResponse,
  JobStatusResponse,
  BatchResultsResponse,
  CheckPeriodResponse,
  ClearCachesResponse,
  EntityType,
  InitUploadFile,
  InitUploadResponse,
  FinalizeFile,
} from '../types/dataUpload';

// ============================================================================
// BATCH CRUD
// ============================================================================

/**
 * @deprecated Use initUpload() + uploadFileToGCS() + finalizeBatch() instead (signed URL flow).
 * This legacy function uploads via multipart/form-data which is limited by Cloud Run's 32MB body limit.
 */
export async function createBatch(params: {
  entityType: EntityType;
  period: string;
  files: File[];
  ratingEntityId?: string;
  riskModelId?: string;
}): Promise<UploadBatch> {
  const formData = new FormData();
  formData.append('entityType', params.entityType);
  formData.append('period', params.period);
  if (params.ratingEntityId) {
    formData.append('ratingEntityId', params.ratingEntityId);
  }
  if (params.riskModelId) {
    formData.append('riskModelId', params.riskModelId);
  }
  for (const file of params.files) {
    formData.append('files', file);
  }

  return upload<UploadBatch>('/api/data-upload/batches', formData);
}

/**
 * List upload batches with optional filters.
 */
export async function listBatches(params?: {
  limit?: number;
  status?: string;
  entityType?: string;
}): Promise<ListBatchesResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.status) searchParams.set('status', params.status);
  if (params?.entityType) searchParams.set('entityType', params.entityType);

  const query = searchParams.toString();
  const url = `/api/data-upload/batches${query ? `?${query}` : ''}`;
  return get<ListBatchesResponse>(url);
}

/**
 * Get a single batch by ID.
 */
export async function getBatch(batchId: string): Promise<UploadBatch> {
  return get<UploadBatch>(`/api/data-upload/batches/${batchId}`);
}

/**
 * Delete a batch (only when status=uploaded).
 */
export async function deleteBatch(batchId: string): Promise<DeleteBatchResponse> {
  return del<DeleteBatchResponse>(`/api/data-upload/batches/${batchId}`);
}

// ============================================================================
// PREFLIGHT APPROVAL
// ============================================================================

/**
 * Approve a batch that is in preflight_review status.
 * Transitions it to 'uploaded' so Stage 1 can proceed.
 */
export async function approvePreflight(batchId: string): Promise<UploadBatch> {
  return post<UploadBatch>(`/api/data-upload/batches/${batchId}/approve-preflight`);
}

// ============================================================================
// PIPELINE STAGES
// ============================================================================

/**
 * Trigger Stage 1: Load CSV data into BigQuery.
 */
export async function runStage1(batchId: string): Promise<StageTriggerResponse> {
  return post<StageTriggerResponse>(`/api/data-upload/batches/${batchId}/run-stage1`);
}

/**
 * Trigger Stage 2: Calculate financial indicators.
 */
export async function runStage2(batchId: string): Promise<StageTriggerResponse> {
  return post<StageTriggerResponse>(`/api/data-upload/batches/${batchId}/run-stage2`);
}

/**
 * Trigger Stage 3: Calculate CAMEL ratings.
 */
export async function runStage3(batchId: string): Promise<StageTriggerResponse> {
  return post<StageTriggerResponse>(`/api/data-upload/batches/${batchId}/run-stage3`);
}

/**
 * Get aggregated job status for all pipeline stages.
 */
export async function getJobStatus(batchId: string): Promise<JobStatusResponse> {
  return get<JobStatusResponse>(`/api/data-upload/batches/${batchId}/job-status`);
}

// ============================================================================
// RESULTS & CACHE
// ============================================================================

/**
 * Get pipeline results summary for a completed batch.
 * Returns rating distribution from BigQuery.
 */
export async function getBatchResults(batchId: string): Promise<BatchResultsResponse> {
  return get<BatchResultsResponse>(`/api/data-upload/batches/${batchId}/results`);
}

/**
 * Check if data already exists for a given period.
 * Used to show a duplicate-period warning before upload.
 */
export async function checkPeriod(period: string): Promise<CheckPeriodResponse> {
  return get<CheckPeriodResponse>(`/api/data-upload/check-period?period=${encodeURIComponent(period)}`);
}

/**
 * Clear all backend data caches so new pipeline results appear immediately.
 */
export async function clearCaches(): Promise<ClearCachesResponse> {
  return post<ClearCachesResponse>('/api/data-upload/clear-caches');
}

// ============================================================================
// SIGNED URL UPLOAD (for files > 32MB)
// ============================================================================

/**
 * Initialize a signed-URL upload.
 * Returns GCS signed PUT URLs for each file so the frontend can upload
 * directly to GCS, bypassing Cloud Run's 32MB body limit.
 */
export async function initUpload(params: {
  entityType: EntityType;
  period: string;
  files: InitUploadFile[];
  ratingEntityId?: string;
  riskModelId?: string;
}): Promise<InitUploadResponse> {
  return post<InitUploadResponse>('/api/data-upload/init-upload', params);
}

/**
 * Upload a single file directly to GCS using a signed URL.
 * Returns when the upload is complete.
 */
export async function uploadFileToGCS(
  signedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during GCS upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.send(file);
  });
}

/**
 * Finalize a signed-URL upload batch.
 * Called after all files have been uploaded directly to GCS.
 */
export async function finalizeBatch(params: {
  batchId: string;
  entityType: EntityType;
  period: string;
  files: FinalizeFile[];
  ratingEntityId?: string;
  riskModelId?: string;
}): Promise<UploadBatch> {
  const { batchId, ...body } = params;
  return post<UploadBatch>(`/api/data-upload/batches/${batchId}/finalize`, body);
}
